import type {
  HOOK_EVENT_TYPE_OPTIONS,
  NON_SELECTABLE_HOOK_EVENT_TYPE_OPTIONS,
} from "../constants/automation";
import type { IAutomationShareInfo } from "./automation-share";
// NOTE: NSAI-4270 — the automation endpoints return only the owner-identity
// fields IEntityOwner carries. The server restricts the `user` relation at query
// level, so no other user column (password hash, auth / recovery tokens, billing
// ids) is ever present on these responses. NSAI-3491 moved the shape itself to
// types/user.ts, where the probe endpoints share it.
import type { IEntityOwner } from "./user";

export interface ICronScheduleAt {
  kind: "at";
  // NOTE: ISO 8601 timestamp
  at: string;
}

export interface ICronScheduleEvery {
  kind: "every";
  everyMs: number;
  // NOTE: NSAI-6123 — `null` is part of the type because stored rows carry it
  // and `isKnownCronSchedule` narrows to this type after accepting one. Leaving
  // it out made the predicate promise more than it establishes: a consumer
  // could write `anchorMs !== undefined` and then call a number method on null.
  anchorMs?: number | null | undefined;
}

export interface ICronScheduleCron {
  kind: "cron";
  // NOTE: 5-field cron expression
  expr: string;
  // NOTE: IANA timezone. Nullable for the same reason as `anchorMs` above —
  // stored rows carry `null`, and the narrowing predicate accepts them.
  tz?: string | null | undefined;
}

export type TCronSchedule =
  | ICronScheduleAt
  | ICronScheduleEvery
  | ICronScheduleCron;

// NOTE: NSAI-5272 — the automation kinds that can be shared with a member or
// published to the marketplace. Mirrors EAutomationType on the server; the
// values are the ones stored in automation_shares.automation_type and
// automation_marketplace_listings.automation_type.
export enum EAutomationType {
  CRON_JOB = "cron_job",
  WEBHOOK = "webhook",
}

export enum ECronRunStatus {
  OK = "ok",
  ERROR = "error",
  SKIPPED = "skipped",
  RUNNING = "running",
}

export interface ICronJob {
  uuid: string;
  userUuid: string;
  projectUuid: string;
  name: string;
  description: string | null;
  enabled: boolean;
  schedule: TCronSchedule;
  prompt: string;
  modelUuid: string;
  assistantUuid: string | null;
  nextRunAt: string | null;
  lastRunAt: string | null;
  lastStatus: string | null;
  lastError: string | null;
  lastDurationMs: number | null;
  consecutiveErrors: number;
  isRunning: boolean;
  timeoutSeconds: number;
  // NOTE: NSAI-6395 — the Agent Skills this automation pins, pre-loaded ahead of
  // the catalog on every run. Null means none are pinned, which is also what an
  // automation configured before pinning existed reports.
  requestedSkillUuids: string[] | null;
  // NOTE: NSAI-6501 — "update the same file": every run replaces ONE generated
  // file (same uuid, so its shares stay) instead of adding a new one.
  // `sameFileUuid` is that file, null until the first run produces one.
  // Optional because a server that predates the flag leaves it out; both
  // clients read a missing flag as off.
  updateSameFile?: boolean;
  sameFileUuid: string | null;
  createdAt: string;
  updatedAt: string;
  user?: IEntityOwner;
  // NOTE: NSAI-5272 — present (non-null) when this row reached the caller
  // through a share rather than ownership. Null for automations the caller owns
  // and for every row an automation manager sees.
  share?: IAutomationShareInfo | null;
}

// NOTE: NSAI-4937 — the fields the Agents list search reads. Structural rather
// than a union of ICronJob | IWebhook | IHook so the predicate covers all three
// tabs (and an event rule, which cannot be shared, simply has no `share`).
export interface ISearchableAutomation {
  name: string;
  user?: IEntityOwner | null;
  share?: IAutomationShareInfo | null;
}

export interface IMatchesAutomationSearchProps {
  automation: ISearchableAutomation;
  search: string;
}

export interface IBulkToggleCronJobPayload {
  uuids: string[];
  enabled: boolean;
}

export interface IBulkDeleteCronJobPayload {
  uuids: string[];
}

export type TBulkToggleWebhooksPayload = IBulkToggleCronJobPayload;

export type TBulkDeleteWebhooksPayload = IBulkDeleteCronJobPayload;

export type TBulkToggleHooksPayload = IBulkToggleCronJobPayload;

export type TBulkDeleteHooksPayload = IBulkDeleteCronJobPayload;

export interface IBulkOperationResultItem {
  uuid: string;
  isSuccess: boolean;
  reason?: string;
}

export interface IBulkOperationResult {
  results: IBulkOperationResultItem[];
}

export interface IRunGeneratedFile {
  uuid: string;
  name: string;
  type: string;
  fileSize: number | null;
}

// NOTE: NSAI-5266 — one integration a run used (provider + optional account label),
// deduped. Populated on the run row's `integrations` summary for the history list.
export interface IAgentRunIntegration {
  provider: string;
  account?: string;
  // NOTE: NSAI-5688 — how many tool calls this run made on this
  // provider+account. Recorded server-side so the run LIST can badge each chip
  // immediately; deriving it client-side needed the step trace, which is only
  // fetched when the user expands a run. Optional: runs recorded before this
  // field carry no count and simply show none.
  count?: number;
}

// NOTE: NSAI-5266 — one recorded step of an agent run's trace (a tool call). Dates
// are ISO strings over the wire. `input` is the truncated tool args and `output` a
// bounded, truncated preview of the tool's result (never the full/raw result).
// `toolFamily` may be null; the client derives the icon from `toolName` when so.
export interface IAgentRunStep {
  uuid: string;
  runUuid: string;
  runType: string;
  stepOrder: number;
  stepType: string;
  toolName: string | null;
  toolFamily: string | null;
  provider: string | null;
  accountLabel: string | null;
  input: unknown | null;
  output: unknown | null;
  status: string;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  createdAt: string;
}

// NOTE: NSAI-5266 — the per-run detail payload from `.../runs/:runUuid`: the run row
// plus its ordered step trace (empty for legacy step-less runs).
export interface IAgentRunDetail<TRun> {
  run: TRun;
  steps: IAgentRunStep[];
}

export interface ICronRun {
  uuid: string;
  cronJobUuid: string;
  status: string;
  error: string | null;
  output: string | null;
  durationMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  // NOTE: NSAI-5266 — optional (grouped last): legacy rows created before this
  // feature have neither. `triggerType` ∈ EAutomationRunTriggerType, `integrations`
  // is the deduped run-level summary.
  triggerType?: string | null;
  integrations?: IAgentRunIntegration[] | null;
  generatedFiles?: IRunGeneratedFile[];
  // NOTE: NSAI-4036 — the run's USD cost at the prices in force when it ran.
  // `null` means the run is not priceable — an unpriced model, token counts that
  // never arrived, or a row written before the column existed — and renders as a
  // dash. It is never "free". Optional only because an older server omits the
  // key entirely; every row a current server returns carries it, null included.
  estimatedCostUsd?: number | null;
  // NOTE: NSAI-6948 — true when the run timed out and its tokens, cost and
  // integrations cover only the work done before the timeout (label them as
  // partial). Null or absent on every other run.
  isUsagePartial?: boolean | null;
}

export interface ICronRunsListResponse {
  data: ICronRun[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IWebhookRunsListResponse {
  data: IWebhookRun[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface IHookRunsListResponse {
  data: IHookRun[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// NOTE: Webhooks

export interface IWebhook {
  uuid: string;
  userUuid: string;
  projectUuid: string;
  name: string;
  description: string | null;
  enabled: boolean;
  // NOTE: NSAI-5272 — empty string when the caller reached this webhook through
  // a share: the token is the webhook's secret and stays with its owner (and
  // with automation managers). Clients hide the copy-URL action when it is
  // empty.
  token: string;
  payloadTemplate: string | null;
  prompt: string;
  modelUuid: string;
  assistantUuid: string | null;
  timeoutSeconds: number;
  // NOTE: NSAI-6395 — the Agent Skills this automation pins, pre-loaded ahead of
  // the catalog on every run. Null means none are pinned, which is also what an
  // automation configured before pinning existed reports.
  requestedSkillUuids: string[] | null;
  createdAt: string;
  updatedAt: string;
  user?: IEntityOwner;
  // NOTE: NSAI-5272 — see ICronJob.share.
  share?: IAutomationShareInfo | null;
}

export interface IWebhookRun {
  uuid: string;
  webhookUuid: string;
  status: string;
  error: string | null;
  output: string | null;
  durationMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  requestPayload: Record<string, unknown> | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  // NOTE: NSAI-5266 — optional (grouped last) for pre-feature rows (see ICronRun).
  triggerType?: string | null;
  integrations?: IAgentRunIntegration[] | null;
  generatedFiles?: IRunGeneratedFile[];
  // NOTE: NSAI-4036 — the run's USD cost at the prices in force when it ran.
  // `null` means the run is not priceable — an unpriced model, token counts that
  // never arrived, or a row written before the column existed — and renders as a
  // dash. It is never "free". Optional only because an older server omits the
  // key entirely; every row a current server returns carries it, null included.
  estimatedCostUsd?: number | null;
  // NOTE: NSAI-6948 — see ICronRun.isUsagePartial.
  isUsagePartial?: boolean | null;
}

export interface ICreateWebhookPayload {
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  payloadTemplate?: string | undefined;
  prompt: string;
  modelUuid: string;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
}

export interface IUpdateWebhookPayload {
  name?: string | undefined;
  description?: string | undefined;
  enabled?: boolean | undefined;
  payloadTemplate?: string | undefined;
  prompt?: string | undefined;
  modelUuid?: string | undefined;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
}

// NOTE: Hooks (event rules)

export interface IHook {
  uuid: string;
  userUuid: string;
  projectUuid: string;
  name: string;
  description: string | null;
  eventType: string;
  conditions: Record<string, unknown> | null;
  enabled: boolean;
  prompt: string;
  modelUuid: string;
  assistantUuid: string | null;
  timeoutSeconds: number;
  // NOTE: NSAI-6395 — the Agent Skills this automation pins, pre-loaded ahead of
  // the catalog on every run. Null means none are pinned, which is also what an
  // automation configured before pinning existed reports.
  requestedSkillUuids: string[] | null;
  createdAt: string;
  updatedAt: string;
  user?: IEntityOwner;
}

export interface IHookRun {
  uuid: string;
  hookUuid: string;
  status: string;
  error: string | null;
  output: string | null;
  durationMs: number | null;
  promptTokens: number | null;
  completionTokens: number | null;
  eventPayload: Record<string, unknown> | null;
  startedAt: string;
  finishedAt: string | null;
  createdAt: string;
  // NOTE: NSAI-5266 — optional (grouped last) for pre-feature rows (see ICronRun).
  triggerType?: string | null;
  integrations?: IAgentRunIntegration[] | null;
  generatedFiles?: IRunGeneratedFile[];
  // NOTE: NSAI-4036 — the run's USD cost at the prices in force when it ran.
  // `null` means the run is not priceable — an unpriced model, token counts that
  // never arrived, or a row written before the column existed — and renders as a
  // dash. It is never "free". Optional only because an older server omits the
  // key entirely; every row a current server returns carries it, null included.
  estimatedCostUsd?: number | null;
  // NOTE: NSAI-6948 — see ICronRun.isUsagePartial.
  isUsagePartial?: boolean | null;
}

export interface ICreateHookPayload {
  name: string;
  description?: string | undefined;
  eventType: string;
  conditions?: Record<string, unknown> | undefined;
  enabled?: boolean | undefined;
  prompt: string;
  modelUuid: string;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
}

export interface IUpdateHookPayload {
  name?: string | undefined;
  description?: string | undefined;
  eventType?: string | undefined;
  conditions?: Record<string, unknown> | undefined;
  enabled?: boolean | undefined;
  prompt?: string | undefined;
  modelUuid?: string | undefined;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
}

// NOTE: Cron Jobs

export interface ICreateCronJobPayload {
  name: string;
  description?: string | undefined;
  enabled?: boolean | undefined;
  schedule: TCronSchedule;
  prompt: string;
  modelUuid: string;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
  // NOTE: NSAI-6501 — keep ONE generated file up to date. Scheduled agents
  // only; the webhook and event-rule payloads have no such field.
  updateSameFile?: boolean | undefined;
}

export interface IUpdateCronJobPayload {
  name?: string | undefined;
  description?: string | undefined;
  enabled?: boolean | undefined;
  schedule?: TCronSchedule | undefined;
  prompt?: string | undefined;
  modelUuid?: string | undefined;
  assistantUuid?: string | undefined;
  timeoutSeconds?: number | undefined;
  // NOTE: NSAI-6395 — the pinned Agent Skills. Omit to leave them unchanged; an
  // empty array clears the selection. Capped server-side at
  // MAX_EXPLICIT_SKILL_IDS, the same limit the chat picker enforces.
  requestedSkillUuids?: string[] | undefined;
  // NOTE: NSAI-6501 — keep ONE generated file up to date. Scheduled agents
  // only; the webhook and event-rule payloads have no such field.
  updateSameFile?: boolean | undefined;
}

export interface IDescribeCronExpressionProps {
  expression: string;
  /** NOTE: Platform language (i18n.language). Falls back to English. */
  locale?: string | undefined;
}

export interface IInterpretSchedulePayload {
  text: string;
  /** NOTE: The model chosen in the form — interpretation runs on it exclusively. */
  modelUuid: string;
  locale?: string | undefined;
  timezone?: string | undefined;
}

export interface IBuildScheduleReadingKeyProps {
  modelUuid: string;
  text: string;
}

export interface IScheduleInterpretationResult {
  /** NOTE: Croner-validated, or null when the phrase could not be read. Never rendered. */
  expression: string | null;
  timezone?: string | undefined;
}

// NOTE: The i18n keys carried by HOOK_EVENT_TYPE_OPTIONS (constants/automation.ts).
// Kept as the literal union rather than `string` so `translate()` — whose key
// argument is typed from the resource bundle on mobile — accepts the result of
// `getHookEventTypeLabelKey` without a cast.
export type THookEventTypeLabelKey =
  | (typeof HOOK_EVENT_TYPE_OPTIONS)[number]["labelKey"]
  | (typeof NON_SELECTABLE_HOOK_EVENT_TYPE_OPTIONS)[number]["labelKey"];
